import React from "react";
import { Redirect } from "react-router-dom";

import Login from "../pages/authentication/Login";
import MFASignIn from "../pages/authentication/MFASignIn";
import SupportLogin from "../pages/support-login/SupportLogin";

const ForgotPassword = React.lazy(() => import("../pages/authentication/ForgotPassword"));
const NewPassword = React.lazy(() => import("../pages/authentication/NewPassword"));
const Terms = React.lazy(() => import("../pages/terms/Terms"));
const ChangePassword = React.lazy(() => import("../pages/authentication/ChangePassword"));
const RequestedWithdrawalList = React.lazy(
  () => import("../pages/requested-withdrawal/RequestedWithdrawalList/RequestedWithdrawalList"),
);
const SelectClient = React.lazy(() => import("../pages/settings/select-client/SelectClient"));
const ClientLimitList = React.lazy(() => import("../pages/settings/client-limit/ClientLimitList/ClientLimitList"));
const UserBankDataList = React.lazy(() => import("../pages/user-bank-data/UserBankDataList/UserBankDataList"));
const UserDetailsList = React.lazy(() => import("../pages/user-details/UserDetailsList/UserDetailsList"));

const GeneratePaymentLink = React.lazy(() => import("../pages/external/GeneratePaymentLink"));
const MFASetup = React.lazy(() => import("../pages/authentication/MFASetup"));
const PasswordChanged = React.lazy(() => import("../pages/authentication/PasswordChanged"));
const AccountBlocked = React.lazy(() => import("../pages/authentication/AccountBlocked"));
const TransactionList = React.lazy(
  () => import("../pages/transaction-list/TransactionsList/TransactionListWithShared"),
);
const TrustedUsersList = React.lazy(() => import("../pages/trusted-users/TrustedUsersList/TrustedUsersList"));
const MonthlyStatementList = React.lazy(
  () => import("../pages/reporting/monthly-statement/MonthlyStatementList/MonthlyStatementList"),
);
const NameCheckList = React.lazy(() => import("../pages/reporting/name-check/NameCheckList/NameCheckList"));
const UsersLastDepositList = React.lazy(
  () => import("../pages/reporting/users-last-deposit/UserLastDepositList/UserLastDepositList"),
);
const UsersCardsList = React.lazy(() => import("../pages/reporting/users-cards/UsersCardsList/UserCardsList"));
const UsersList = React.lazy(() => import("../pages/settings/users/UsersList/UsersList"));
const ClientCredentialsList = React.lazy(
  () => import("../pages/settings/credentials/ClientCredentialsList/ClientCredentialsList"),
);
const WebhookList = React.lazy(() => import("../pages/settings/webhooks/WebhookList"));
const ProvidersList = React.lazy(() => import("../pages/settings/providers/ProvidersList/ProvidersList"));
const EmailTemplateList = React.lazy(() => import("../pages/settings/email/EmailTemplateList/EmailTemplateList"));
const NewEmailTemplateV2 = React.lazy(() => import("../pages/settings/email/EmailTemplateList/NewEmailTemplate"));
const ProviderSortList = React.lazy(() => import("../pages/settings/provider-sort/ProviderSortList/ProviderSortList"));
const EditProviderSortV2 = React.lazy(
  () => import("../pages/settings/provider-sort/ProviderSortList/EditProviderSort"),
);
const ProviderRulesList = React.lazy(
  () => import("../pages/settings/provider-rules/ProviderRulesList/ProviderRulesList"),
);
const SettingsV2 = React.lazy(() => import("../pages/settings/settings/Settings"));
const AvailableProvidersV2 = React.lazy(
  () => import("../pages/admin-settings/provider-groups/AvailableProviders/AvailableProviders"),
);
const StyleV2 = React.lazy(() => import("../pages/settings/style/Style/Style"));
const ProviderErrorMessageList = React.lazy(
  () => import("../pages/settings/provider-error-messages/ProviderErrorMessageList"),
);
const AdminApiKeyList = React.lazy(() => import("../pages/settings/admin-api-keys/AdminApiKeyList"));
const CheckoutTranslationList = React.lazy(
  () => import("../pages/settings/checkout-translations/CheckoutTranslationList"),
);
const EditCheckoutTranslation = React.lazy(
  () => import("../pages/settings/checkout-translations/EditCheckoutTranslation"),
);
const AuditLogList = React.lazy(() => import("../pages/settings/audit-logs/AuditLogList"));
const UserSecurityList = React.lazy(() => import("../pages/settings/security/UserSecurityList/UserSecurityList"));
const Dashboard = React.lazy(() => import("../pages/dashboard/Dashboard/Dashboard"));
const RoutingGroupList = React.lazy(() => import("../pages/settings/routing/RoutingList/RoutingGroupList"));
const ClientProfilePage = React.lazy(() => import("../pages/settings/vendors/ClientProfile/ClientProfilePage"));
const UserProfilePage = React.lazy(() => import("../pages/user-profile/UserProfile/UserProfilePage"));
const BlockList = React.lazy(() => import("../pages/blocklist/Blocklist/BlockList"));
const TransactionFailuresList = React.lazy(
  () => import("../pages/reporting/transaction-failures/TransactionFailuresList"),
);
const ReportGenerationList = React.lazy(() => import("../pages/reporting/generated-reports/ReportGenerationList"));
const TransactionListGlobal = React.lazy(() => import("../pages/global-search/TransactionListGlobalWithShared"));
const CustomerWalletsList = React.lazy(() => import("../pages/nixxe-crypto/customer-wallets/CustomerWalletsList"));
const TransactionHistoryList = React.lazy(
  () => import("../pages/nixxe-crypto/transaction-history/TransactionHistoryList"),
);
const WalletAddressList = React.lazy(() => import("../pages/nixxe-crypto/address/WalletAddressList"));
const CryptoDashboard = React.lazy(() => import("../pages/nixxe-crypto/dashboard/DashboardV2"));
const UserSegments = React.lazy(() => import("../pages/user-segments/UserSegment/UserSegments"));
const SharedCardActivity = React.lazy(() => import("../pages/shared-card-activity/SharedCardActivity"));
const SupportExpired = React.lazy(() => import("../pages/support-login/SupportExpired"));

const userRoutes = [
  // support grant
  { path: "/support-login", component: SupportLogin },
  { path: "/support-expired", component: SupportExpired },

  // auth
  { path: "/login", component: Login },
  { path: "/forgot_password", component: ForgotPassword },
  { path: "/new_password", component: NewPassword },
  { path: "/mfa_signin", component: MFASignIn },
  { path: "/mfa_setup", component: MFASetup },
  { path: "/change_password", component: ChangePassword },
  { path: "/generate_payment_links", component: GeneratePaymentLink },
  { path: "/monthly-statement", component: MonthlyStatementList },
  { path: "/users-cards", component: UsersCardsList },
  { path: "/name-check", component: NameCheckList },
  { path: "/users-last-deposit", component: UsersLastDepositList },
  { path: "/generated-reports", component: ReportGenerationList },
  { path: "/password_changed", component: PasswordChanged },
  { path: "/account_blocked", component: AccountBlocked },
  { path: "/blocklist", component: BlockList },
  { path: "/user-segments", component: UserSegments },

  // nixxe crypto
  { path: "/crypto-dashboard", component: CryptoDashboard },
  { path: "/customer-wallets", component: CustomerWalletsList },
  { path: "/transaction-history", component: TransactionHistoryList },
  { path: "/wallet-address", component: WalletAddressList },

  // profile
  { path: "/user-profile", component: UserProfilePage },

  // settings
  { path: "/settings", component: SettingsV2 },
  { path: "/transaction-list", component: TransactionList },
  { path: "/global-search", component: TransactionListGlobal },
  { path: "/clients/profile/:id", component: ClientProfilePage },

  // admin settings
  { path: "/available-providers", component: AvailableProvidersV2 },

  // { path: "/clients", component: Vendors },
  { path: "/users", component: UsersList },
  { path: "/providers", component: ProvidersList },
  { path: "/credentials", component: ClientCredentialsList },
  { path: "/webhooks", component: WebhookList },
  { path: "/clients", component: SelectClient },
  { path: "/security", component: UserSecurityList },

  { path: "/provider-rules", component: ProviderRulesList },
  { path: "/client-limit", component: ClientLimitList },
  { path: "/user-bank-data", component: UserBankDataList },

  { path: "/trusted-users", component: TrustedUsersList },
  { path: "/user-details", component: UserDetailsList },
  { path: "/shared-card-activity", component: SharedCardActivity },

  { path: "/routing", component: RoutingGroupList },

  { path: "/style", component: StyleV2 },
  { path: "/provider-sort/:id", component: EditProviderSortV2 },
  { path: "/provider-sort", component: ProviderSortList },

  // email template
  { path: "/email/template/:id", component: NewEmailTemplateV2 },
  { path: "/email", component: EmailTemplateList },
  { path: "/requested-withdrawal", component: RequestedWithdrawalList },

  { path: "/provider-error-messages", component: ProviderErrorMessageList },
  { path: "/admin-api-keys", component: AdminApiKeyList },
  { path: "/checkout-translations/:locale", component: EditCheckoutTranslation },
  { path: "/checkout-translations", component: CheckoutTranslationList },
  { path: "/audit-logs", component: AuditLogList },
  { path: "/transaction-failures", component: TransactionFailuresList },

  // terms
  { path: "/terms", component: Terms },

  // Dashboard
  { path: "/dashboard", component: Dashboard },

  {
    path: "/",
    exact: true,
    component: () => {
      const inSupportMode = Boolean(localStorage.getItem("support_grant"));
      return <Redirect to={inSupportMode ? "/transaction-list" : "/users"} />;
    },
  },
];

export { userRoutes };
